/**
 * How close the nearest thing ahead is, as one number.
 *
 * `src/chart/forwardDangers.ts` answers *what* is in the boat's path. This
 * answers *how urgent it is*, on a 0–1 scale, so that a sound and a colour can
 * both be driven from one reading and cannot disagree with each other or with
 * the banner.
 *
 * Three rules shape it, and all three come from elsewhere in the app:
 *
 * - **A caution is not a danger.** Shoal water the boat can float over is
 *   capped well below full alert, and it never gets the danger colour. A tone
 *   that sounded the same for "you can pass over this" and "you cannot go here"
 *   would undo the whole reason the magenta ring came off every rock on the
 *   chart.
 * - **Time, not distance, is the urgency.** Two hundred metres is a long way at
 *   two knots and fifteen seconds at fifteen. The level is taken from time to
 *   run wherever there is one, and falls back to distance only when there is
 *   not. It is deliberately *not* scaled to the prediction horizons: a skipper
 *   who switches the ten-minute horizon on has not asked to be alarmed ten
 *   minutes out.
 * - **Silence is not clearance.** The forward scan reads rendered tiles, so a
 *   level of zero means "nothing found in the loaded chart ahead" and never
 *   "clear water". Every surface that plays or draws this has to say so.
 *
 * Pure, and takes a structural hit rather than a `ForwardDanger`: core may not
 * import from `src/chart` (rule 2), and the arithmetic has no business being in
 * the same file as an AudioContext.
 */

/**
 * The minimum a scan result has to look like to be ranked.
 *
 * `ForwardDanger` satisfies this structurally, so the caller passes its own
 * dangers straight in and gets them back with their own type intact.
 */
export interface ProximityHit {
  severity: 'danger' | 'caution';
  /** Metres ahead. */
  distanceM: number;
  /** Seconds to reach it at the current speed, or null when not moving usefully. */
  timeToS: number | null;
}

/**
 * One point on the alert curve: how strong the alert is at a given time to run.
 *
 * The curve is what the skipper configures, and everything downstream reads it —
 * the click rate and the tint are both just this number rendered differently.
 * Interpolating between the points, rather than each output shaping its own
 * response, is what keeps them saying the same thing.
 */
export interface AlertStage {
  /** Time to run at which this strength is reached, seconds. */
  timeToS: number;
  /** Strength at that time, 0–1. */
  level: number;
}

/**
 * The default curve: almost nothing until a minute out, then hard.
 *
 * The shape matters more than the numbers. An alert that ramps evenly from five
 * minutes spends most of its life as a low mutter, and a low mutter that is
 * always there is not a warning — it is a texture, and it gets switched off. So
 * the first three points are barely perceptible and exist only to say "something
 * is out there"; the whole rise happens between one minute and thirty seconds,
 * which is when there is still time to alter course and no longer any time to
 * think about it.
 *
 * Every one of these is editable in Settings → Display. They are defaults, not
 * rules: a displacement boat at six knots and a RIB at thirty want different
 * curves, and neither is wrong.
 */
export const DEFAULT_ALERT_STAGES: readonly AlertStage[] = [
  { timeToS: 300, level: 0.04 },
  { timeToS: 120, level: 0.10 },
  { timeToS: 60, level: 0.25 },
  { timeToS: 30, level: 1 },
];

/** How many points the curve has. Fixed, so the settings UI is a fixed grid. */
export const ALERT_STAGE_COUNT = DEFAULT_ALERT_STAGES.length;

/** Longest time to run a stage may be set to, seconds. Half an hour. */
export const MAX_STAGE_TIME_S = 1800;

/**
 * Speed assumed when the scan reports no time to run, metres per second.
 *
 * Ten knots. This is a backstop and nothing more: a forward cone needs a speed
 * to have a length at all, so in practice there is always a time. Converting the
 * distance to a pretend time and running it through the same curve is better
 * than keeping a second, differently-shaped distance ramp around — a second
 * curve is a second thing to configure and a second thing to disagree with the
 * first.
 */
export const NOMINAL_SPEED_MS = 5;

/**
 * Ceiling on what a caution may reach.
 *
 * Half. Shoal water you can float over must be audibly and visibly a different
 * thing from water you cannot enter, at every range, including alongside it.
 */
export const CAUTION_MAX_LEVEL = 0.5;

/** Slowest and fastest click rates, per second. */
export const MIN_CLICK_HZ = 0.5;
export const MAX_CLICK_HZ = 14;

/**
 * Where the tint is drawn.
 *
 * `bar` is a strip across the top of the screen and touches no chart at all.
 * `screen` is the vignette: red creeping in from the edges, with the middle left
 * alone. The bar is the default because it is the honest one — it cannot shift a
 * single charted colour, and a skipper reading depth shading under a red cast is
 * exactly the failure mode worth designing out.
 */
export type TintStyle = 'bar' | 'screen';

export const TINT_STYLES: readonly TintStyle[] = ['bar', 'screen'];

/**
 * Strongest each style gets.
 *
 * The bar can go nearly opaque because it is over nothing — no chart, no text,
 * no symbol. The vignette cannot, because at full strength it is sitting on top
 * of the depth shading and the magenta danger marks, and changing how those read
 * is the opposite of helpful.
 */
export const MAX_TINT_OPACITY: Record<TintStyle, number> = {
  bar: 0.95,
  screen: 0.55,
};

/**
 * The collision alert: the last few seconds, said differently.
 *
 * The proximity alert answers "how close"; this answers "you are about to hit
 * it". A rising click rate and a deepening colour are both *continuous* — they
 * are good at conveying a trend and poor at conveying a deadline, and by the time
 * the trend has gone as far as it goes there is nothing left for it to do. A
 * flash is discontinuous. It is a different kind of signal, which is the point:
 * it cannot be mistaken for more of the same.
 *
 * It fires on **dangers only**, never on a shoal caution, however close. Water
 * the boat can float over is not a collision, and spending the loudest signal in
 * the app on it would undo the whole caution tier — the same rule as the magenta
 * ring, one tier further up.
 */
export interface CollisionSettings {
  enabled: boolean;
  /** Time to run at or inside which it fires, seconds. */
  timeToS: number;
  /** Flashes per second. */
  flashHz: number;
}

export const DEFAULT_COLLISION: CollisionSettings = {
  enabled: false,
  timeToS: 12,
  flashHz: 2,
};

/**
 * How soon and how fast the collision alert may be set to fire.
 *
 * **The 3 Hz ceiling is not a taste decision.** WCAG 2.3.1 puts the
 * photosensitive-seizure risk band at more than three general flashes in any one
 * second, and this is a full-width band or a full-screen wash — the largest area
 * the app can flash. A skipper cannot consent on behalf of everyone who will
 * later look at the screen, so the limit is enforced rather than advised.
 *
 * The time ceiling of 60 s is there because past a minute this stops being a
 * deadline and becomes the proximity alert with extra steps.
 */
export const COLLISION_LIMITS = {
  timeToS: { min: 3, max: 60 },
  flashHz: { min: 0.5, max: 3 },
} as const;

/**
 * Whether the collision alert should be firing.
 *
 * Takes the hit rather than the level so it reads the *time*, not the strength.
 * The two normally agree, but the alert curve is editable — a skipper who sets
 * the last stage to two minutes has asked for an early warning, not for the
 * collision flash to start two minutes out.
 */
export function isCollision(
  alert: Pick<ProximityAlert, 'severity' | 'hit'>,
  collision: CollisionSettings,
): boolean {
  if (!collision.enabled) return false;
  // Never for a caution. Floating over a shoal is not a collision.
  if (alert.severity !== 'danger' || !alert.hit) return false;

  const seconds = alert.hit.timeToS !== null && Number.isFinite(alert.hit.timeToS)
    ? alert.hit.timeToS
    : alert.hit.distanceM / NOMINAL_SPEED_MS;

  return seconds <= collision.timeToS;
}

/** Milliseconds per flash cycle, clamped into the safe rate band. */
export function flashPeriodMs(flashHz: number): number {
  const hz = Math.min(
    COLLISION_LIMITS.flashHz.max,
    Math.max(COLLISION_LIMITS.flashHz.min, Number.isFinite(flashHz) ? flashHz : DEFAULT_COLLISION.flashHz),
  );
  return 1000 / hz;
}

/**
 * How much of the screen the tint leaves alone, as a percentage of the radius
 * from the centre out.
 *
 * Set by looking at it. At 30% the vignette reached far enough in to visibly
 * shift the pale blues of the depth shading in the mid-field — not enough to
 * confuse deep water with the caution band, but enough that the claim "the
 * middle keeps its true colours" was doing more work than it had earned. The
 * corners still go strongly red at full alert, which is where the effect comes
 * from; nothing about the warning is weaker for this.
 */
export const TINT_CLEAR_RADIUS_PCT = 42;

export interface ProximityAlert<T extends ProximityHit = ProximityHit> {
  /** 0 for nothing found, 1 for as urgent as this gets. */
  level: number;
  /** Which tier the level came from, or null when there is nothing ahead. */
  severity: 'danger' | 'caution' | null;
  /** What drove it, so a panel can name the same thing the sound means. */
  hit: T | null;
}

export const NO_ALERT: ProximityAlert = { level: 0, severity: null, hit: null };

export interface ProximityOptions {
  /**
   * Let shoal cautions raise the alert, capped at `CAUTION_MAX_LEVEL`.
   *
   * Off means the alert is about water the boat cannot use and nothing else —
   * the quieter setting, and the one to choose in a shoal-strewn place where
   * the caution tier is firing constantly.
   */
  includeCautions: boolean;
  /** The configured curve. Defaults to `DEFAULT_ALERT_STAGES`. */
  stages?: readonly AlertStage[];
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Put a set of stages into the order and shape the interpolation needs.
 *
 * Sorted by descending time — furthest out first — and then each level forced to
 * be at least the one before it. **An alert that gets quieter as you close is
 * worse than no alert**, so a curve that dips is repaired rather than honoured.
 * The settings UI cannot easily prevent it (the fields are edited one at a time,
 * and every intermediate state would be rejected), so it is fixed here, where
 * every reader goes through.
 */
export function normaliseStages(stages: readonly AlertStage[]): AlertStage[] {
  const usable = stages
    .filter((stage) => Number.isFinite(stage.timeToS) && Number.isFinite(stage.level)
      && stage.timeToS >= 0)
    .map((stage) => ({ timeToS: stage.timeToS, level: clamp01(stage.level) }))
    .sort((a, b) => b.timeToS - a.timeToS);

  if (usable.length === 0) return [...DEFAULT_ALERT_STAGES];

  let floor = 0;
  for (const stage of usable) {
    stage.level = Math.max(stage.level, floor);
    floor = stage.level;
  }
  return usable;
}

/**
 * Strength of the alert at a given time to run, 0–1.
 *
 * Piecewise linear between the configured points, so the curve the skipper set
 * is the curve they get and the movement between stages is still smooth. Beyond
 * the furthest stage there is no alert at all; inside the nearest one it holds,
 * because "closer than full alert" is still full alert.
 */
export function levelForTime(
  timeToS: number,
  stages: readonly AlertStage[] = DEFAULT_ALERT_STAGES,
): number {
  const points = normaliseStages(stages);
  if (!Number.isFinite(timeToS)) return 0;
  const time = Math.max(0, timeToS);

  if (time > points[0].timeToS) return 0;

  for (let i = 1; i < points.length; i++) {
    const far = points[i - 1];
    const near = points[i];
    if (time <= near.timeToS) continue;
    // Between two stages. A zero-width span would divide by zero; two stages at
    // the same time mean the nearer one wins, which is the step the skipper
    // asked for by setting them equal.
    const span = far.timeToS - near.timeToS;
    if (span <= 0) return near.level;
    return near.level + (far.level - near.level) * ((time - near.timeToS) / span);
  }

  return points[points.length - 1].level;
}

/** How urgent one hit is, 0–1. */
export function hitLevel(
  hit: ProximityHit,
  stages: readonly AlertStage[] = DEFAULT_ALERT_STAGES,
): number {
  // No reported time: pretend a speed rather than keep a second curve around.
  // See NOMINAL_SPEED_MS — this is a backstop, not the normal path.
  const seconds = hit.timeToS !== null && Number.isFinite(hit.timeToS)
    ? hit.timeToS
    : hit.distanceM / NOMINAL_SPEED_MS;

  const raw = levelForTime(seconds, stages);
  return hit.severity === 'caution' ? raw * CAUTION_MAX_LEVEL : raw;
}

/**
 * The alert for a whole scan: whichever hit reads loudest.
 *
 * By level rather than by distance, which matters when a caution sits nearer
 * than a danger. A shoal fifty metres off does not out-shout a rock two hundred
 * metres off, because the cap says it cannot — intended behaviour, not a
 * rounding artefact. The banner picks the nearest *danger* to describe for the
 * same reason.
 */
export function proximityAlert<T extends ProximityHit>(
  hits: readonly T[],
  options: ProximityOptions = { includeCautions: true },
): ProximityAlert<T> {
  let best: ProximityAlert<T> = { level: 0, severity: null, hit: null };

  for (const hit of hits) {
    if (hit.severity === 'caution' && !options.includeCautions) continue;
    const level = hitLevel(hit, options.stages ?? DEFAULT_ALERT_STAGES);
    if (level <= best.level) continue;
    best = { level, severity: hit.severity, hit };
  }

  return best;
}

/**
 * Milliseconds between clicks at a given level, or null for silence.
 *
 * Geometric, not linear: a rate rising in equal steps sounds like almost
 * nothing happening until the last moment. Doubling every fifth of the scale is
 * what makes the change audible while there is still time to act on it — and it
 * is why this is a click track rather than a beep. A beep says "something"; a
 * rising click rate says "closer".
 */
export function clickIntervalMs(level: number): number | null {
  if (!(level > 0)) return null;
  const hz = MIN_CLICK_HZ * (MAX_CLICK_HZ / MIN_CLICK_HZ) ** clamp01(level);
  return 1000 / hz;
}

/**
 * Opacity of the tint at a given level, 0 when there is nothing ahead.
 *
 * Linear in the level, deliberately. This used to be squared, to hold the tint
 * back through most of an approach — but that shaping now lives in the stage
 * curve, where the skipper can see it and change it. Two curves in series is one
 * curve too many: the settings would say the alert reaches a quarter strength at
 * a minute out and the screen would show a sixteenth.
 */
export function tintOpacity(level: number, style: TintStyle = 'bar'): number {
  return clamp01(level) * MAX_TINT_OPACITY[style];
}

/**
 * Colour the tint reaches at full strength, as a CSS colour.
 *
 * Red for a danger, amber for a caution. Not magenta: that belongs to the
 * charted danger symbol and to nothing else, and a full-screen magenta wash
 * would be the loudest possible violation of that rule.
 */
export function tintColour(severity: ProximityAlert['severity']): string {
  return severity === 'caution' ? 'rgb(224, 150, 0)' : 'rgb(208, 24, 24)';
}

/**
 * Pitch of the click, hertz.
 *
 * A caution clicks lower than a danger, so the two are told apart with the
 * phone in a pocket and without looking. The danger click also rises a little
 * as it closes — a small effect, but it is what stops a stream of identical
 * ticks from disappearing into the engine note.
 */
export function clickFrequencyHz(
  severity: ProximityAlert['severity'],
  level: number,
): number {
  return severity === 'caution' ? 900 : 1600 + 900 * clamp01(level);
}
